import { useState, useEffect } from 'react'
import { toast } from 'react-toastify'

import { BillingGroup, PaymentSchedule } from '../models/Billing'
import { CompanyWithInvoiceCode } from '../containers/Admin/Property/types'
import { axiosInstance } from '../helpers'

export const useBillingGroups = () => {
    const [billingGroups, setBillingGroups] = useState<BillingGroup[] | null>(
        null,
    )
    const [isLoading, setIsLoading] = useState(false)
    const [isError, setIsError] = useState(false)
    const [error, setError] = useState<Error | null>(null)

    const fetchBillingGroups = async () => {
        setIsLoading(true)
        setIsError(false)
        setError(null)

        try {
            const response = await axiosInstance.get(
                'billing/billing-groups/',
                {
                    params: {
                        active: 'all',
                    },
                },
            )
            setBillingGroups(response.data)
        } catch (err) {
            setIsError(true)
            setError(err as Error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchBillingGroups()
    }, [])

    const updateBillingGroup = async (data: UpdateBillingGroupRequest) => {
        try {
            await axiosInstance.patch(
                `billing/billing-groups/${data.billing_group_id}/`,
                data.body,
            )
            toast.success('Billing group updated successfully')

            // Update the state directly instead of refetching
            if (billingGroups) {
                setBillingGroups(
                    billingGroups.map((bg) =>
                        bg.id === data.billing_group_id
                            ? { ...bg, ...data.body }
                            : bg,
                    ),
                )
            }
        } catch (err: any) {
            const error = err as Error
            toast.error(
                error.message ||
                    'An error occurred while updating the billing group',
            )
            throw error
        }
    }

    const removeCompanyFromBillingGroup = async (
        data: RemoveCompanyFromBillingGroupRequest,
    ) => {
        try {
            await axiosInstance.post(
                `billing/billing-groups/${data.billing_group_id}/remove-apartment/`,
                {
                    apartment_id: data.company_id,
                },
            )

            // Update the state directly instead of refetching
            if (billingGroups) {
                setBillingGroups(
                    billingGroups.map((bg) =>
                        bg.id === data.billing_group_id
                            ? {
                                  ...bg,
                                  companies: bg.companies.filter(
                                      (company: any) =>
                                          company.id !== data.company_id,
                                  ),
                              }
                            : bg,
                    ),
                )
            }
        } catch (err: any) {
            const error = err as Error
            toast.error(
                error.message ||
                    'An error occurred while removing the company from the billing group',
            )
            throw error
        }
    }

    const addCompanyToBillingGroup = async (
        data: AddCompanyToBillingGroupRequest,
        companyToAdd?: CompanyWithInvoiceCode,
    ) => {
        try {
            await axiosInstance.post(
                `billing/billing-groups/${data.billing_group_id}/add-apartment/`,
                {
                    apartment_id: data.company_id,
                },
            )

            // Update the state directly instead of refetching
            if (billingGroups && companyToAdd) {
                setBillingGroups(
                    billingGroups.map((bg) =>
                        bg.id === data.billing_group_id
                            ? {
                                  ...bg,
                                  companies: [...bg.companies, companyToAdd],
                              }
                            : bg,
                    ),
                )
            }
        } catch (err) {
            const error = err as Error
            toast.error(
                error.message ||
                    'An error occurred while adding the company to the billing group',
            )
            throw error
        }
    }

    const createBillingGroup = async (data: CreateBillingGroupRequest) => {
        try {
            const response = await axiosInstance.post(
                'billing/billing-groups/',
                data,
            )
            toast.success('Billing group created successfully')

            const newBillingGroup: BillingGroup = response.data
            if (billingGroups) {
                setBillingGroups([...billingGroups, newBillingGroup])
            }
        } catch (err: any) {
            // Check if this is a QuickBooks authorization error
            if (
                err.response?.data?.requires_authorization &&
                err.response?.data?.authorize_url
            ) {
                toast.error(
                    'QuickBooks authorization required. Redirecting to authorization page...',
                )
                // Redirect to the authorization URL
                window.location.href = err.response.data.authorize_url
                return
            }

            const error = err as Error
            toast.error(
                error.message ||
                    'An error occurred while creating the billing group',
            )
            throw error
        }
    }

    const createInvoice = async (data: CreateInvoiceRequest) => {
        try {
            const response = await axiosInstance.post(
                'billing/create-invoice/',
                data,
            )
            toast.success('Invoice created successfully')
            let newInvoiceDate = new Date().toISOString()
            if (data.issue_date) {
                newInvoiceDate = data.issue_date
            }
            if (billingGroups) {
                setBillingGroups(
                    billingGroups.map((bg) =>
                        bg.id === data.billing_group_id
                            ? { ...bg, last_invoice_date: newInvoiceDate }
                            : bg,
                    ),
                )
            }
            return response.data
        } catch (err: any) {
            const error = err as Error
            toast.error(
                error.message || 'An error occurred while creating the invoice',
            )
            throw error
        }
    }

    return {
        billingGroups,
        isLoading,
        isError,
        error,
        refetch: fetchBillingGroups,
        updateBillingGroup,
        removeCompanyFromBillingGroup,
        addCompanyToBillingGroup,
        createBillingGroup,
        createInvoice,
    }
}

export interface UpdateBillingGroupRequest {
    billing_group_id: number
    body: {
        payment_schedule?: PaymentSchedule
        invoice_email?: string
        active?: boolean
    }
}

export interface RemoveCompanyFromBillingGroupRequest {
    billing_group_id: number
    company_id: number
}

export interface AddCompanyToBillingGroupRequest {
    billing_group_id: number
    company_id: number
}

export interface CreateBillingGroupRequest {
    apartment_ids: number[]
    invoice_email: string
    payment_schedule: PaymentSchedule
    customer_name: string
}

export interface CreateInvoiceRequest {
    billing_group_id: number
    issue_date?: string
    note?: string
}
